import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';

import {
  Client,
  CommandDataOut,
  CommandQueryDataIn,
  CommandStatus,
  PageInfo,
  PermissionsEnum,
  SortingType,
} from '../../../api/api-reference';
import { IsNewDirective } from '../../../directives/is-new.directive';
import { AuthService } from '../../../services/auth.service';
import { SignalRService } from '../../../services/signalr.service';
import { DatepickerComponent } from '../../core/components/datepicker/datepicker.component';
import { SearchComponent } from '../../core/components/search/search.component';
import { SortComponent } from '../../core/components/sort/sort.component';
import { StatusComponent } from '../../core/components/status/status.component';
import { DateRangeInput } from '../../core/models/date-range-input';
import { PaginationComponent } from '../../core/components/pagination/pagination.component';
import { ClipboardService } from '../../../services/clipboard.service';
import { SignalRBaseComponent } from '../../../services/signalr-base.component';
import { processDateRange } from '../../../helper/date-utils';

@Component({
  selector: 'app-on-demand-instantaneous',
  standalone: true,
  imports: [
    TranslateModule,
    CommonModule,
    AngularSvgIconModule,
    DatepickerComponent,
    SortComponent,
    SearchComponent,
    RouterLink,
    PaginationComponent,
    IsNewDirective,
    StatusComponent,
  ],
  templateUrl: './on-demand-instantaneous.component.html',
  styleUrl: './on-demand-instantaneous.component.scss',
})
export class OnDemandInstantaneousComponent extends SignalRBaseComponent {
  public pageSize: number = 10;
  public pageNumber: number = 1;
  public currentPage: number = 1;
  public numberOfCommands: number = 0;
  public lastPageSize: number = 1;
  commandStatuses = Object.values(CommandStatus).map(s => s.toUpperCase());

  data: CommandDataOut[] | undefined = [];
  isDatepickerOpen = false;
  readonly dayInMs = 24 * 60 * 60 * 1000;
  PermissionEnum = PermissionsEnum;

  requestData = new CommandQueryDataIn({
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.pageNumber }),
    search: undefined,
    startDate: undefined,
    endDate: undefined,
    sorting: SortingType.Descending,
  });

  constructor(
    private client: Client,
    private toastr: ToastrService,
    private router: Router,
    public authService: AuthService,
    signalRService: SignalRService,
    public clipboardService: ClipboardService
  ) {
    super(signalRService);
    this.reloadTable(1);
  }

  printResponseOfCommand(event: MouseEvent, cmd: CommandDataOut) {
    if (cmd.status === CommandStatus.Failed) {
      event.stopPropagation();
      this.toastr.error(cmd.errorMessage);
    }
  }

  retryCommand(cmd: CommandDataOut) {
    this.client.onDemandInstantaneousRetry(cmd.id!).subscribe({
      next: () => {
        this.toastr.success('Command retry initiated successfully');
        this.reloadTable(this.currentPage);
      },
      error: (err: string) => {
        this.toastr.error('Failed to retry command: ' + err);
      },
    });
  }
  handleRowClick(
    commandId: string | undefined,
    commandStatus: CommandStatus | undefined,
  ) {
    if (commandStatus === CommandStatus.Success)
      this.router.navigate(['on-demand/instantaneous-object-details'], {
        queryParams: { commandId: commandId },
      });
  }

  protected override registerHandlers(): void {
    this.registerSignalREvent('instantaneous-on-demand', (message) => {
      const item = this.data?.find((item) => item.id === message.commandId);
      if (item) {
        item.status = message.commandStatus;
        if (message.commandStatus == CommandStatus.Failed || message.commandStatus == CommandStatus.ErrorOccured) {
          try {
            const parsed = JSON.parse(message.errorMessage);
            item.errorMessage = parsed.Message;
          } catch {
            item.errorMessage = message.errorMessage;
          }
        }
      }
    });

    // Handle the online-status broadcast silently
    this.registerSignalREvent('online-status', () => {});
  }

  override async ngOnInit(): Promise<void> {
    await super.ngOnInit();
  }
  reloadTable(page?: number) {
    this.pageNumber = page || 0;
    if (page) this.requestData!.pageInfo!.page = this.pageNumber;
    this.client
      .onDemandInstantaneousObjectCommands(this.requestData)
      .subscribe({
        next: (response) => {
          this.data = response.data;
          this.numberOfCommands = response.count || 0;
          this.lastPageSize = Math.ceil(this.numberOfCommands / this.pageSize);
        },
        error: (err) => {
          this.toastr.error(err);
        },
      });
  }

  pageChange(value: number) {
    this.currentPage = value;
    this.reloadTable(this.currentPage);
  }

  searchChange(searchText: string) {
    this.requestData.search = searchText;
    this.reloadTable(1);
  }

  toggleModal() {
    this.isDatepickerOpen = !this.isDatepickerOpen;
  }

  onSortOption(selectedSort?: SortingType) {
    if (selectedSort) this.requestData.sorting = selectedSort;
    this.reloadTable();
  }

  onStatusOption(selectedStatus?: string) {
    if (selectedStatus !== undefined) {
      this.requestData.status =
        selectedStatus === 'ALL'
          ? undefined
          : (selectedStatus as CommandStatus);
    }
    this.reloadTable();
  }

  selectedDate: string | null = '';
  onDateRangeChanged(input: DateRangeInput): void {
    const result = processDateRange(input);

    this.requestData.startDate = result.startDate;
    this.requestData.endDate = result.endDate;
    this.selectedDate = result.selectedDate;
    this.filterDatesInTable();
  }

  clearDates(): void {
    this.requestData.startDate = undefined;
    this.requestData.endDate = undefined;
    this.reloadTable();
  }

  filterDatesInTable() {
    this.client
      .onDemandInstantaneousObjectCommands(this.requestData)
      .subscribe({
        next: (response) => {
          this.data = response.data;
          this.numberOfCommands = response.count || 0;
          this.lastPageSize = Math.ceil(this.numberOfCommands / this.pageSize);
        },
        error: (err) => {
          this.toastr.error(err);
        },
      });
  }
}
